/**
 * Depreciation Calculator — ACV (Actual Cash Value) engine for insurance export.
 *
 * Uses straight-line depreciation with category-specific useful-life rates,
 * condition modifiers, and a 20% RCV floor.
 *
 * Sources: Insurance industry standard rates (Nationwide / ClaimsPages guide).
 * Spec reference: manifest-pdf-export-spec-v3.md
 */

export type ItemCondition = "Excellent" | "Good" | "Fair" | "Poor";

export interface DepreciationTableEntry {
  /** Useful life in years */
  usefulLifeYears: number;
  /** Annual depreciation rate (0–1) */
  annualRate: number;
}

/**
 * Category-level depreciation table used when a more specific subcategory rate
 * is not available. Matches the spec's simplified table.
 */
export const DEPRECIATION_TABLE: Record<string, DepreciationTableEntry> = {
  Electronics:     { usefulLifeYears: 5,  annualRate: 0.20 },
  Furniture:       { usefulLifeYears: 10, annualRate: 0.10 },
  Appliances:      { usefulLifeYears: 10, annualRate: 0.10 },
  Clothing:        { usefulLifeYears: 5,  annualRate: 0.20 },
  Jewelry:         { usefulLifeYears: 25, annualRate: 0.04 },
  Tools:           { usefulLifeYears: 10, annualRate: 0.10 },
  "Sporting Goods":{ usefulLifeYears: 7,  annualRate: 1 / 7 },
  Other:           { usefulLifeYears: 7,  annualRate: 1 / 7 },
};

/** ACV floor — ACV is never less than this fraction of RCV */
const ACV_FLOOR = 0.20;

/** Condition depreciation add-ons (applied on top of straight-line depreciation) */
const CONDITION_MODIFIERS: Partial<Record<ItemCondition, number>> = {
  Fair: 0.10,
  Poor: 0.20,
};

export interface CalculateACVInput {
  replacementCost: number;
  /** The year the item was purchased (e.g. 2018). If absent, returns null. */
  purchaseYear: number | null | undefined;
  /** Category name, matched case-insensitively against DEPRECIATION_TABLE keys */
  category?: string;
  condition?: ItemCondition;
}

export interface ACVResult {
  acv: number;
  depreciationPercent: number;
}

/**
 * Calculate Actual Cash Value (ACV) using straight-line depreciation.
 *
 * Returns null when purchaseYear is missing — PDF renderer should fall back to
 * displaying only the replacement cost.
 */
export function calculateACV(input: CalculateACVInput): ACVResult | null {
  const { replacementCost, purchaseYear, category, condition } = input;

  if (purchaseYear == null) return null;

  const currentYear = new Date().getFullYear();
  const age = Math.max(0, currentYear - purchaseYear);

  // Look up category entry (case-insensitive)
  const entry = resolveDepreciationEntry(category);
  const { usefulLifeYears, annualRate } = entry;

  // Base straight-line depreciation (capped at useful life)
  let depreciationRate = Math.min(annualRate * age, annualRate * usefulLifeYears);

  // Apply condition modifier
  const conditionAddon = condition ? (CONDITION_MODIFIERS[condition] ?? 0) : 0;
  depreciationRate = Math.min(depreciationRate + conditionAddon, 1 - ACV_FLOOR);

  // Apply floor: ACV never below 20% of RCV
  const acv = Math.max(
    replacementCost * (1 - depreciationRate),
    replacementCost * ACV_FLOOR,
  );

  const depreciationPercent = Math.round((1 - acv / replacementCost) * 100);

  return {
    acv: Math.round(acv),
    depreciationPercent,
  };
}

function resolveDepreciationEntry(category?: string): DepreciationTableEntry {
  if (!category) return DEPRECIATION_TABLE.Other;

  // Try exact match first
  if (DEPRECIATION_TABLE[category]) return DEPRECIATION_TABLE[category];

  // Case-insensitive fallback
  const lower = category.toLowerCase();
  for (const [key, entry] of Object.entries(DEPRECIATION_TABLE)) {
    if (key.toLowerCase() === lower) return entry;
  }

  return DEPRECIATION_TABLE.Other;
}
